import { publicNews, type ContentLocale, type PublicContentRecord } from './public-catalog'
import { contentRoutes } from '@/i18n/content-routes'

export interface NewsSectionCopy {
  heading?: string
  paragraphs?: string[]
  items?: string[]
  linkLabel?: string
}

export interface NewsCopy {
  title: string
  excerpt: string
  sections: NewsSectionCopy[]
  mediaAlts: string[]
  videoLabels?: string[]
  sourceLanguage?: ContentLocale
}

export interface NewsArticle extends PublicContentRecord {
  videoUrls?: readonly string[]
  externalUrls?: Partial<Record<ContentLocale, readonly string[]>>
  preservePrimaryMedia?: boolean
}

const articleExtras: Record<string, Pick<NewsArticle, 'videoUrls' | 'externalUrls' | 'preservePrimaryMedia'>> = {
  'news-amcli-2026': { preservePrimaryMedia: true },
  'news-simti-2026-workshop-esamed': { preservePrimaryMedia: true },
  'news-simti-2026': { preservePrimaryMedia: true },
  'news-medlab-middle-east-2024': {
    preservePrimaryMedia: true,
    externalUrls: {
      it: ['', '', '', '', 'https://www.linkedin.com/company/esamed-srl/?viewAsMember=true'],
      en: ['', '', '', '', 'https://www.linkedin.com/company/esamed-srl/?viewAsMember=true'],
    },
  },
  'news-nuovi-prodotti-di-preanalitica-decapper-e-recapper': {
    preservePrimaryMedia: true,
    videoUrls: [
      'https://www.youtube.com/watch?v=_jwx3RP_aFM',
      'https://www.youtube.com/watch?v=slfGfGmT3q8',
    ],
  },
  'news-convegno-poct-udine': { preservePrimaryMedia: true },
  'news-articolo-de-osservatorio-gruppo24ore': { preservePrimaryMedia: true },
  'news-medica-2022': { preservePrimaryMedia: true },
  'news-scivac-rimini-2022': { preservePrimaryMedia: true },
  'news-fornitura-emergency-poct': {
    preservePrimaryMedia: true,
    externalUrls: {
      it: ['https://www.emergency.it/'],
      en: ['https://en.emergency.it/'],
    },
  },
  'news-cpa-easynat-real-time-molecular-diagnostic-system': { preservePrimaryMedia: true },
}

export const newsArticles: readonly NewsArticle[] = publicNews.map((article) => ({
  ...article,
  ...articleExtras[article.id],
}))

export function localizedNewsRecord(article: NewsArticle, language: ContentLocale) {
  return article.languages[language] ?? article.languages.it ?? article.languages.en
}

export function findNewsBySlug(slug: string | null, language: ContentLocale) {
  if (!slug) return null
  const localized = newsArticles.find((article) => article.languages[language]?.slug === slug)
  if (localized) return localized
  const explicitFallback = newsArticles.find((article) => !article.languages[language] && (article.languages.it?.slug === slug || article.languages.en?.slug === slug))
  if (explicitFallback) return explicitFallback
  const mapped = contentRoutes.find((route) => route.type === 'news' && new URL(route[language], 'https://local.invalid').searchParams.get('article') === slug)
  return mapped ? newsArticles.find((article) => article.id === mapped.id) ?? null : null
}

export function getNewsTranslationKey(id: string) {
  return `newsContent.${id.replace(/^news-/, '').replace(/-/g, '_')}`
}

export function isSafeEditorialUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}
